import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


import PropertyDetailsTab from '../../../pages/property-listing-creation-and-management/components/PropertyDetailsTab';
import LocationTab from '../../../pages/property-listing-creation-and-management/components/LocationTab';
import PricingTab from '../../../pages/property-listing-creation-and-management/components/PricingTab';
import OwnerTab from '../../../pages/property-listing-creation-and-management/components/OwnerTab';
import MediaTab from '../../../pages/property-listing-creation-and-management/components/MediaTab';
import { propertyValidation } from '../../../utils/validationUtils';

const PropertyFormModal = ({ isOpen, onClose, onSave, property = null, mode = 'add' }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    propertyId: property?.propertyId || `PROP-${Date.now()}`,
    title: property?.title || '',
    propertyType: property?.type || '',
    purpose: property?.purpose || '',
    bedrooms: property?.bedrooms || '',
    bathrooms: property?.bathrooms || '',
    area: property?.area || '',
    floor: property?.floor || '',
    furnishing: property?.furnishing || '',
    yearBuilt: property?.yearBuilt || '',
    description: property?.remarks || '',
    features: property?.features || [],
    address: property?.address || '',
    city: property?.city || '',
    district: property?.district || '',
    latitude: property?.lat || '31.9454',
    longitude: property?.lng || '35.9284',
    ownerName: property?.owner || '',
    ownerPhone: property?.ownerPhone || '',
    ownerEmail: property?.ownerEmail || '',
    askingPrice: property?.askingPrice || '',
    salePrice: property?.salePrice || '',
    clientRetainerPercentage: property?.clientRetainerPercentage || '',
    ownerRetainerPercentage: property?.ownerRetainerPercentage || '',
    price: property?.price || '',
    currency: property?.currency || 'JOD',
    commission: property?.commission || '',
    negotiable: property?.negotiable || false,
    status: property?.status || 'متاح',
    customFields: property?.customFields || {},
    images: property?.images || [],
    videos: property?.videos || []
  });

  const tabs = [
    { id: 'details', label: 'تفاصيل العقار', icon: 'Building2' },
    { id: 'location', label: 'الموقع', icon: 'MapPin' },
    { id: 'owner', label: 'بيانات المالك', icon: 'User' },
    { id: 'pricing', label: 'التسعير', icon: 'DollarSign' },
    { id: 'media', label: 'الصور والفيديو', icon: 'Image' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // CLIENT-SIDE VALIDATION: Clear error for this field when user types
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    // CLIENT-SIDE VALIDATION: Validate before saving
    const validation = propertyValidation?.validate(formData);
    
    if (!validation?.isValid) {
      setValidationErrors(validation?.errors);
      
      // Show error message
      const firstError = Object.values(validation?.errors)?.[0];
      alert(`خطأ في البيانات: ${firstError}`);
      
      // Switch to tab with first error
      const errorField = Object.keys(validation?.errors)?.[0];
      if (['title', 'propertyType', 'purpose', 'bedrooms', 'bathrooms', 'area', 'floor', 'furnishing', 'yearBuilt', 'description', 'features']?.includes(errorField)) {
        setActiveTab('details');
      } else if (['address', 'city', 'district', 'latitude', 'longitude']?.includes(errorField)) {
        setActiveTab('location');
      } else if (['ownerName', 'ownerPhone', 'ownerEmail']?.includes(errorField)) {
        setActiveTab('owner');
      } else if (['price', 'askingPrice', 'salePrice', 'commission', 'clientRetainerPercentage', 'ownerRetainerPercentage', 'currency', 'negotiable']?.includes(errorField)) {
        setActiveTab('pricing');
      } else if (errorField?.startsWith('image_') || ['images', 'videos']?.includes(errorField)) {
        setActiveTab('media');
      }
      
      return;
    }

    setIsSaving(true);
    
    try {
      await onSave({
        ...formData,
        id: property?.id || `prop${Date.now()}`,
        lastUpdate: new Date()?.toISOString()
      });
      
      setValidationErrors({});
      onClose();
    } catch (error) {
      console.error('Error saving property:', error);
      alert(`فشل في حفظ العقار: ${error?.message || 'خطأ غير معروف'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.15, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.1, ease: 'easeIn' }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95,
      y: 30
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.95,
      y: 30,
      transition: { 
        duration: 0.2,
        ease: 'easeIn'
      }
    }
  };

  const tabContentVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.2, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0, 
      x: -20,
      transition: { duration: 0.15, ease: 'easeIn' }
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative bg-card rounded-lg border border-border shadow-warm-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
            variants={modalVariants}
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 md:p-6 border-b border-border flex items-center justify-between bg-muted/30">
              <div className="flex items-center gap-3">
                <Icon 
                  name={mode === 'add' ? 'Plus' : 'Edit'} 
                  size={24} 
                  color="var(--color-primary)" 
                />
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                    {mode === 'add' ? 'إضافة عقار جديد' : 'تعديل العقار'}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formData?.propertyId}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                iconSize={20}
                onClick={onClose}
              />
            </div>

            {/* Tabs */}
            <div className="border-b border-border bg-muted/20 px-4 md:px-6">
              <div className="flex gap-1 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={tabContentVariants}
                >
                  {activeTab === 'details' && (
                    <PropertyDetailsTab
                      formData={formData}
                      onChange={handleInputChange}
                      errors={validationErrors}
                      onBlur={handleInputChange}
                    />
                  )}
                  {activeTab === 'location' && (
                    <LocationTab
                      formData={formData}
                      onChange={handleInputChange}
                      errors={validationErrors}
                      onBlur={handleInputChange}
                    />
                  )}
                  {activeTab === 'owner' && (
                    <OwnerTab
                      formData={formData}
                      onChange={handleInputChange}
                      errors={validationErrors}
                      onBlur={handleInputChange}
                    />
                  )}
                  {activeTab === 'pricing' && (
                    <PricingTab
                      formData={formData}
                      onChange={handleInputChange}
                      errors={validationErrors}
                      onBlur={handleInputChange}
                    />
                  )}
                  {activeTab === 'media' && (
                    <MediaTab
                      formData={formData}
                      onChange={handleInputChange}
                      errors={validationErrors}
                      onBlur={handleInputChange}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer */}
            <motion.div 
              className="p-4 md:p-6 border-t border-border bg-muted/30 flex items-center justify-between gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.2 }}
            >
              <div className="text-xs text-muted-foreground">
                {isSaving ? 'جاري الحفظ...' : Object.keys(validationErrors)?.length > 0 ? 'يرجى تصحيح الأخطاء قبل الحفظ' : 'جميع الحقول المطلوبة يجب تعبئتها'}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isSaving}
                >
                  إلغاء
                </Button>
                <Button
                  variant="default"
                  iconName="Save"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'جاري الحفظ...' : mode === 'add' ? 'إضافة العقار' : 'حفظ التغييرات'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PropertyFormModal;